import { ref } from "vue";
import type { PurchaseOrder } from "../../redistribution/models/PurchaseOrder.js";

interface SearchResultsSortStateItem {
  colId: string;
  sort?: "asc" | "desc" | null;
  sortIndex?: number | null;
}

interface PoSearchResultsContext {
  mode: "flat" | "single-po-related" | "vendor-search";
  primaryPoNumber: string | undefined;
  relatedPurchaseOrders: PurchaseOrder[];
  helperMessage: string | undefined;
  helperMessageTone: "info" | "error" | undefined;
  vendorName?: string;
}

const createDefaultPoSearchResultsContext = (): PoSearchResultsContext => ({
  mode: "flat",
  primaryPoNumber: undefined,
  relatedPurchaseOrders: [],
  helperMessage: undefined,
  helperMessageTone: undefined,
});

export function createSearchResultsModule() {
  const searchResults = ref<PurchaseOrder[] | undefined>(undefined);
  const searchSelectedPoNumbers = ref<string[]>([]);
  const searchPoNumbersInput = ref<string>("");
  const searchSelectedPurchaseOrders = ref<PurchaseOrder[]>([]);
  const searchResultsSortState = ref<SearchResultsSortStateItem[]>([]);
  const poSearchResultsContext = ref<PoSearchResultsContext>(
    createDefaultPoSearchResultsContext()
  );
  const lastSearchTypeLabel = ref<string | undefined>(undefined);
  const activeSearchLoadingMessage = ref<string | undefined>(undefined);
  const activeSearchNoResultsMessage = ref<string | undefined>(undefined);

  function setSearchResults(results: PurchaseOrder[]): void {
    searchResults.value = results;
  }

  function setSearchSelectedPoNumbers(poNumbers: string[]): void {
    searchSelectedPoNumbers.value = poNumbers;
  }

  function setSearchSelectedPurchaseOrders(
    purchaseOrders: PurchaseOrder[]
  ): void {
    searchSelectedPurchaseOrders.value = purchaseOrders;
  }

  function setSearchResultsSortState(
    sortState: SearchResultsSortStateItem[]
  ): void {
    searchResultsSortState.value = sortState;
  }

  function setPoSearchResultsContext(context: PoSearchResultsContext): void {
    poSearchResultsContext.value = context;
  }

  function resetPoSearchResultsContext(): void {
    poSearchResultsContext.value = createDefaultPoSearchResultsContext();
  }

  function beginTypedSearch(args: {
    searchTypeLabel: string;
    loadingMessage: string;
    noResultsMessage: string;
  }): void {
    lastSearchTypeLabel.value = args.searchTypeLabel;
    activeSearchLoadingMessage.value = args.loadingMessage;
    activeSearchNoResultsMessage.value = args.noResultsMessage;
  }

  function clearTypedSearchPresentation(): void {
    activeSearchLoadingMessage.value = undefined;
  }

  function clearSearchResults(): void {
    searchResults.value = undefined;
    searchSelectedPoNumbers.value = [];
    searchPoNumbersInput.value = "";
    searchSelectedPurchaseOrders.value = [];
    searchResultsSortState.value = [];
    poSearchResultsContext.value = createDefaultPoSearchResultsContext();
  }

  return {
    searchResults,
    searchSelectedPoNumbers,
    searchPoNumbersInput,
    searchSelectedPurchaseOrders,
    searchResultsSortState,
    poSearchResultsContext,
    lastSearchTypeLabel,
    activeSearchLoadingMessage,
    activeSearchNoResultsMessage,
    setSearchResults,
    setSearchSelectedPoNumbers,
    setSearchSelectedPurchaseOrders,
    setSearchResultsSortState,
    setPoSearchResultsContext,
    resetPoSearchResultsContext,
    beginTypedSearch,
    clearTypedSearchPresentation,
    clearSearchResults,
  };
}
